import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import {
  FormControlLabel, FormGroup, IconButton, keyframes, Switch,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { isSafari } from '../../../utils/platform';
import AudioRecorder from '../../molecules/AudioRecorder/AudioRecorder';
import Metronome from '../../molecules/Metronome/Metronome';
import MetronomeControls from '../../molecules/Metronome/MetronomeControls';
import InputDeviceSelector from './InputDeviceSelector';
import Beeper from './beeper';

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
`;

const useStyles = makeStyles()((theme) => ({
  songRecorder: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    alignItems: 'center',
    width: '100%',
  },
  metronome: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  inputDeviceSelect: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    minWidth: '20ch',
    maxWidth: '40ch',
  },
  recordingContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      justifyContent: 'center',
    },
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 3,
  },
  countIn: {
    fontSize: '3rem',
    fontWeight: 'bold',
    animation: `${fadeOut} 2s ${theme.transitions.easing.easeInOut}`,
    animationIterationCount: 1,
    animationFillMode: 'forwards',
  },
  inputControls: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
  },
  metronomeControls: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    alignItems: 'center',
  },
  warning: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.warning.main,
  },
}));

export interface SongRecorderProps {
  onRecordingComplete?: (audioBlob: Blob, bpm?: number,
    timeSig?: number, startTimeOffset?: number) => void;
  onMinDurationReached?: () => void;
  onMaxDurationReached?: () => void;
  allowRecording?: boolean;
  minDuration?: number;
  maxDuration?: number;
}

const SongRecorder: React.FC<SongRecorderProps> = ({
  onRecordingComplete,
  onMaxDurationReached,
  onMinDurationReached,
  minDuration = 5,
  maxDuration = 300,
  allowRecording = true,
}) => {
  const { classes } = useStyles();

  const firstBeatTime = useRef<number>(-1);

  const [useMetronome, setUseMetronome] = useState(false);
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [timeSig, setTimeSig] = useState(4);

  const [countIn, setCountIn] = useState(-1);
  const { t } = useTranslation();

  const [recording, setRecording] = useState(false);
  const [recordAudio, setRecordAudio] = useState(false);

  const [secondInterval, setSecondInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  const [inputDevice, setInputDevice] = useState('');

  const [cantAquireUserMedia, setCantAquireUserMedia] = useState(false);

  useEffect(() => {
    if (useMetronome) {
      setMetronomeOn(true);
    }
  }, [useMetronome]);

  // Stop metronome when recording finished
  useEffect(() => {
    if (!recording) {
      setMetronomeOn(false);
    }
  }, [recording]);

  useEffect(() => {
    if (!allowRecording) {
      if (recordAudio) {
        setRecordAudio(false);
        setRecording(false);
      }
      if (!recordAudio && recording) {
        setRecording(false);
        setCountIn(0);
        if (secondInterval) {
          clearInterval(secondInterval);
          setSecondInterval(null);
        }
      }
    }
  }, [allowRecording, recordAudio, recording, secondInterval]);

  const doCountIn = useCallback(() => {
    setCountIn((prev) => {
      if (prev === 0) {
        if (secondInterval) {
          clearInterval(secondInterval);
          setSecondInterval(null);
        }
        return -1;
      }
      if (prev === 1) {
        firstBeatTime.current = performance.now();
        setRecordAudio(true);
        return 0;
      }
      if (prev > 0) {
        Beeper.playBeep(0.125, 880, 0.1);
        if (prev === 4) {
          setMetronomeOn(true);
        }
        return prev - 1;
      }
      return -1;
    });
  }, [secondInterval]);

  const startCountIn = useCallback(() => {
    setCountIn(4);
    Beeper.playBeep(0.125, 880, 0.1);
    setMetronomeOn(false);
    setSecondInterval((prev) => {
      if (prev) {
        clearInterval(prev);
      }
      let interval = 1000;
      if (useMetronome) {
        interval = (1000 * timeSig) / (bpm / 60);
      }
      return setInterval(doCountIn, interval);
    });
  }, [doCountIn, useMetronome, bpm, timeSig]);

  const toggleRecording = useCallback(() => {
    if (!recording) {
      // get audio device permissions, then start count in
      const constraints = {
        audio: inputDevice ? { deviceId: { exact: inputDevice } } : true,
      };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(() => {
          startCountIn();
          setCantAquireUserMedia(false);
        })
        .catch(() => {
          setRecording(false);
          setCantAquireUserMedia(true);
        });

      setRecording(true);
    } else {
      setRecording(false);
      setRecordAudio(false);
      setCountIn(-1);
      if (secondInterval) {
        clearInterval(secondInterval);
        setSecondInterval(null);
      }
    }
  }, [recording, startCountIn, secondInterval, inputDevice]);

  const handleMinDurationReached = useCallback(() => {
    if (onMinDurationReached) {
      onMinDurationReached();
    }
  }, [onMinDurationReached]);

  const handleMaxDurationReached = useCallback(() => {
    if (recording) {
      toggleRecording();
    }
    if (onMaxDurationReached) {
      onMaxDurationReached();
    }
  }, [onMaxDurationReached, recording, toggleRecording]);

  return (
    <div className={classes.songRecorder}>
      <div className={classes.metronomeControls}>
        <FormGroup>
          <FormControlLabel
            control={(
              <Switch
                id={useMetronome ? 'btn_recording_metronome_off' : 'btn_recording_metronome_on'}
                checked={useMetronome}
                onChange={() => setUseMetronome(!useMetronome)}
              />
            )}
            label={t('song_recorder.metronome')}
          />
        </FormGroup>

        {useMetronome && (
          <div className={classes.metronome}>
            <MetronomeControls
              onBpmChange={setBpm}
              onTimeSigChange={setTimeSig}
              bpm={bpm}
              timeSig={timeSig}
              isOn={metronomeOn}
            />
            <Metronome bpm={bpm} timeSig={timeSig} isOn={metronomeOn} />
          </div>
        )}
      </div>

      <div className={classes.inputControls}>
        <div className={classes.inputDeviceSelect}>
          <InputDeviceSelector onDeviceSelect={setInputDevice} disabled={recording} />
        </div>
      </div>

      <div className={classes.recordingContainer}>
        <IconButton
          onClick={toggleRecording}
          color="primary"
          size="large"
          id={recording ? 'btn_recording_stop' : 'btn_recording_start'}
          disabled={!allowRecording}
        >
          {recording ? (
            <StopIcon fontSize="large" />
          ) : (
            <FiberManualRecordIcon color="error" fontSize="large" />
          )}
        </IconButton>

        {countIn >= 0 && (
          <div className={classes.overlay}>
            <span key={countIn} className={classes.countIn}>
              {countIn === 0 ? t('song_recorder.go') : countIn}
            </span>
          </div>
        )}

        <AudioRecorder
          recording={recordAudio}
          onMaxDurationReached={handleMaxDurationReached}
          onMinDurationReached={handleMinDurationReached}
          minDuration={minDuration}
          maxDuration={maxDuration}
          inputDeviceId={inputDevice}
          mimeType={isSafari() ? 'audio/mp4' : 'audio/webm;codecs=opus'}
          onRecordingComplete={(b, startTime) => {
            if (onRecordingComplete) {
              if (useMetronome) {
                if (firstBeatTime.current >= 0) {
                  const startTimeOffset = firstBeatTime.current - startTime;
                  onRecordingComplete(b, bpm, timeSig, startTimeOffset);
                } else {
                  onRecordingComplete(b, bpm, timeSig);
                }
              } else {
                onRecordingComplete(b);
              }
            }
          }}
        />
      </div>
      {cantAquireUserMedia && (
        <span className={classes.warning}>{t('song_recorder.cant_aquire_user_media')}</span>
      )}
    </div>
  );
};

export default SongRecorder;
